import { ApiTabIndex } from '@/Enums/ReportingDashboard/ApiTabIndex.enum';
import { Role } from '@/Enums/Users/Role.enum';
import { IParsedAdvertiser } from '@/Interfaces/Advertiser.interface';
import { IClientControlOption, IControlOption, IWhitelabelControlOption } from '@/Interfaces/ControlOption.interface';
import { IReportingFilterModel } from '@/Interfaces/Filters/FilterModel.interface';
import { IMappedReportItem, IPublisher, IReportItem } from '@/Interfaces/ReportingDashboard/Publishers.interface';
import { IParsedWhitelabelWithRelations } from '@/Interfaces/Whitelabels/Whitelabel.interface';
import { PUBLISHER_NAME } from '@/constants/reporting';
import { filterAdvertisersList, mapAdvertisersList } from '.';
import { mapWhitelabelToControlOption } from '../Whitelabels';
import { getClientOptions } from '../filters/filterOptions';

type ClientType = IClientControlOption<IParsedAdvertiser>;
type WhitelabelType = IParsedWhitelabelWithRelations<IParsedAdvertiser>;
type FilterSubject = ClientType | WhitelabelType | IParsedAdvertiser;

type FilterFunction<T extends FilterSubject> = (subject: T) => boolean;
type MapperFunction<T extends FilterSubject> = (subject: T) => T;

const ADVERTISER_KEY_MAP: Record<ApiTabIndex, keyof IParsedAdvertiser> = {
  [ApiTabIndex.Omnichannel]: 'beeswaxId',
  [ApiTabIndex.Dooh]: 'neuronId',
  [ApiTabIndex.Google]: 'hasDv360Partner',
  [ApiTabIndex.Search]: 'hasEsmSemAccount',
  [ApiTabIndex.Social]: 'hasEsmSclAccount',
  [ApiTabIndex.Adswizz]: 'adswizzId',
  [ApiTabIndex.Simplifi]: 'hasSimplifiAccount',
  [ApiTabIndex.Gam]: 'hasGamAdvertiser',
};

const advertiserFilter = (api: ApiTabIndex): FilterFunction<IParsedAdvertiser> => {
  return advertiser => !!advertiser[ADVERTISER_KEY_MAP[api]];
};

const clientMapper = (api: ApiTabIndex): MapperFunction<ClientType> => {
  return client => ({ ...client, advertisers: (client.advertisers ?? []).filter(advertiserFilter(api)) });
};

const clientAggregator = (clients: ClientType[], api: ApiTabIndex): ClientType[] => {
  return clients.map(clientMapper(api));
};

const whitelabelMapper = (api: ApiTabIndex): MapperFunction<WhitelabelType> => {
  return whitelabel => ({ ...whitelabel, clients: clientAggregator(whitelabel.clients ?? [], api) });
};

const whitelabelAggregator = (whitelabels: WhitelabelType[], api: ApiTabIndex): WhitelabelType[] => {
  return whitelabels.map(whitelabelMapper(api));
};

export const getClientsList = (apiTab: ApiTabIndex, clients: ClientType[] = []) =>
  clientAggregator(clients ?? [], apiTab);

export const getWhitelabelsList = (apiTab: ApiTabIndex, whitelabels: WhitelabelType[] = []) =>
  whitelabelAggregator(whitelabels ?? [], apiTab);

export const createPublisherLogoMapper = (publishers: IPublisher[]): Record<string, string | undefined> =>
  publishers.reduce(
    (acc, { name, logo }) => {
      acc[name] = logo;
      return acc;
    },
    {} as Record<string, string | undefined>,
  );

// TODO: move to api
export const mapReportWithLogos = (reportData: IReportItem[], publishers: IPublisher[]): IMappedReportItem[] => {
  const publisherLogoMapper: Record<string, string | undefined> = createPublisherLogoMapper(publishers);

  const mappedPubArray: IMappedReportItem[] = reportData.map(item => ({
    ...item,
    Logo: publisherLogoMapper[item[PUBLISHER_NAME]],
  }));

  return mappedPubArray;
};

const getControlOptionValueSet = (options: IControlOption[]) => new Set((options ?? []).map(option => option.value));

export const fulfilledReportCaseHandler = (reportType: string) => (state: any, action: any) => {
  const requestIdVariableName = `${reportType}RequestId`;
  if (state[requestIdVariableName] === action.meta.requestId) {
    state[requestIdVariableName] = null;
    state[reportType] = action.payload?.report;
  }
};

export const getUpdatedTabFilters = (
  apiTab: ApiTabIndex,
  storedFiltersApi: ApiTabIndex,
  storedFilters: IReportingFilterModel<IParsedAdvertiser>,
  whitelabels: IParsedWhitelabelWithRelations<IParsedAdvertiser>[],
  clients: IClientControlOption<IParsedAdvertiser>[],
  userRole: Role,
): IReportingFilterModel<IParsedAdvertiser> => {
  const reuseStored = storedFiltersApi === apiTab;

  if (reuseStored) {
    return { ...storedFilters };
  }

  const storedWhitelabelIds = getControlOptionValueSet(storedFilters.whitelabels ?? []);
  const storedClientIds = getControlOptionValueSet(storedFilters.clients);

  const selectedWhitelabels = whitelabels.filter(wl => storedWhitelabelIds.has(wl.id));
  const clientOptions = getClientOptions({
    userRole,
    clients,
    selectedWhitelabels: selectedWhitelabels.map(mapWhitelabelToControlOption),
  });
  const selectedClients = clientOptions.filter(client => storedClientIds.has(client.value));

  const whitelabelOptions = getWhitelabelsList(apiTab, selectedWhitelabels).map(
    mapWhitelabelToControlOption,
  ) as IWhitelabelControlOption<IParsedAdvertiser>[];

  return {
    ...storedFilters,
    whitelabels: whitelabelOptions,
    clients: getClientsList(apiTab, selectedClients as IClientControlOption<IParsedAdvertiser>[]),
    advertisers: ((storedFilters?.advertisers ?? []).filter(filterAdvertisersList(apiTab)) ?? [])?.map(
      mapAdvertisersList(apiTab),
    ),
    campaigns: [],
  };
};
